import {
  AlertCircle,
  AlertTriangle,
  BellRing,
  Inbox,
  Check,
  CheckCircle2,
  CircleOff,
  Clock3,
  Info,
  LoaderCircle,
  RefreshCw,
  ServerOff,
  ShieldAlert,
  Wrench,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type ReactNode } from "react";
import { formatDateTime, HEALTH_LABELS, relativeTime, SEVERITY_LABELS } from "../lib/format";
import type { HealthState, ProblemSeverity } from "../types";

const HEALTH_ICONS: Record<HealthState, LucideIcon> = {
  operational: CheckCircle2,
  degraded: AlertTriangle,
  outage: CircleOff,
  stale: Clock3,
  maintenance: Wrench,
};

const SEVERITY_ICONS: Record<ProblemSeverity, LucideIcon> = {
  disaster: ShieldAlert,
  high: AlertCircle,
  average: AlertTriangle,
  warning: BellRing,
  information: Info,
  not_classified: CircleOff,
};

export function Button({ className = "", variant = "primary", size = "default", loading, children, disabled, ...props }:
ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger"; size?: "default" | "small"; loading?: boolean }) {
  return (
    <button className={`button button--${variant} button--${size} ${className}`} disabled={disabled || loading} {...props}>
      {loading ? <LoaderCircle className="spin" aria-hidden="true" size={17} /> : null}
      {children}
    </button>
  );
}

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string }>(
  function IconButton({ label, children, className = "", ...props }, ref) {
    return <button ref={ref} className={`icon-button ${className}`} aria-label={label} title={label} {...props}>{children}</button>;
  },
);

export function Card({ className = "", children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <section className={`card ${className}`} {...props}>{children}</section>;
}

export function StatusBadge({ status, compact = false }: { status: HealthState; compact?: boolean }) {
  const Icon = HEALTH_ICONS[status];
  return (
    <span className={`status-badge status-badge--${status}${compact ? " status-badge--compact" : ""}`}>
      <Icon size={compact ? 14 : 16} strokeWidth={2.3} aria-hidden="true" />
      <span>{HEALTH_LABELS[status]}</span>
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: ProblemSeverity }) {
  const Icon = SEVERITY_ICONS[severity];
  return (
    <span className={`severity severity--${severity}`}>
      <Icon size={14} strokeWidth={2.4} aria-hidden="true" />
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

export function DataAge({ value, prefix }: { value: string | null | undefined; prefix?: string }) {
  return (
    <time dateTime={value ?? undefined} title={formatDateTime(value)} className="data-age">
      {prefix ? `${prefix} ` : ""}{relativeTime(value)} <span aria-hidden="true">·</span> {formatDateTime(value)}
    </time>
  );
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {description ? <p className="page-description">{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function LoadingBlock({ rows = 4, label = "Cargando datos" }: { rows?: number; label?: string }) {
  return (
    <div className="loading-block" role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, index) => <span className="skeleton" key={index} />)}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, action }: { icon?: LucideIcon; title: string; description: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon"><Icon size={26} aria-hidden="true" /></span>
      <h2>{title}</h2>
      <p>{description}</p>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <div className="error-state" role="alert">
      <XCircle size={24} aria-hidden="true" />
      <div>
        <strong>No pudimos cargar esta información</strong>
        <p>{error.message}</p>
      </div>
      {onRetry ? <Button variant="secondary" size="small" onClick={onRetry}><RefreshCw size={15} aria-hidden="true" /> Reintentar</Button> : null}
    </div>
  );
}

export function InlineNotice({ tone = "info", title, children }: { tone?: "info" | "success" | "warning" | "danger"; title: string; children?: ReactNode }) {
  const Icon = tone === "success" ? Check : tone === "warning" ? AlertTriangle : tone === "danger" ? ServerOff : Info;
  return (
    <div className={`notice notice--${tone}`} role={tone === "danger" ? "alert" : "status"}>
      <Icon size={19} aria-hidden="true" />
      <div><strong>{title}</strong>{children ? <div>{children}</div> : null}</div>
    </div>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className={`field${error ? " field--error" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
      {error ? <span className="field__error">{error}</span> : null}
    </label>
  );
}

export function ProgressBar({ value, max, label }: { value: number; max: number; label: string }) {
  const percent = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="progress" aria-label={`${label}: ${value} de ${max}`}>
      <div className="progress__bar" style={{ width: `${percent}%` }} />
    </div>
  );
}
